// src/pages/cash/CashDialog.tsx

import React, { useEffect, useState, useMemo } from "react";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Save, Plus, X, Landmark, DollarSign } from "lucide-react"; // Landmark veya banknot ikonu
import { IconTextButton } from "../../../components/ButtonComponents";
import { ETabs } from "../../../components/ETabs";
import { Dropdown } from "primereact/dropdown"; // Para birimi seçimi için
import { Cash, CashPayload } from "../../../types/cash";


// CashPayload tipini doğrudan kullanabiliriz
type CashFormState = CashPayload;

interface CashDialogProps {
  isOpen: boolean;
  onClose: () => void;
  cash: Cash | null;
  onSave: (data: CashPayload) => void; 
}

// Para Birimi Seçenekleri
const CURRENCY_OPTIONS = [
    { label: 'Türk Lirası (TRY)', value: 'TRY' },
    { label: 'ABD Doları (USD)', value: 'USD' },
    { label: 'Euro (EUR)', value: 'EUR' },
];

const initialData: CashFormState = {
  name: "",
  currency: "TRY", // Varsayılan: TRY
};


export const CashDialog: React.FC<CashDialogProps> = ({
  isOpen,
  onClose,
  cash,
  onSave,
}) => {
  const [formData, setFormData] = useState<CashFormState>(initialData);
  const [activeIndex, setActiveIndex] = useState(0); 

   
useEffect(() => {
  if (!isOpen) return;

  if (cash) {
    setFormData({
      name: cash.name || "",
      currency: cash.currency || "TRY",
    });
  } else {
    setFormData(initialData);
  }

  setActiveIndex(0); 

}, [isOpen, cash]);

  // FORM HANDLER
  const handleChange = (field: keyof CashFormState, value: string | undefined) => {
    setFormData((p) => ({ ...p, [field]: value }));
  };
  
const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name) { 
      setActiveIndex(0); 
      console.error("Kasa Adı zorunludur.");
      return;
    }
    
    // Payload'a balance eklemiyoruz, çünkü o sunucuda yönetilmeli.
    const payload: CashPayload = {
        name: formData.name,
        currency: formData.currency || "TRY",
    };

    onSave(payload);
  };

  const tabs = useMemo(() => [
    {
      id: 'basic-info',
      header: <><Landmark className="w-4 h-4 mr-2" /> Kasa Bilgileri</>,
      content: (
        <div className="space-y-4 p-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            
            {/* 1. Kasa Adı */}
            <div className="flex flex-col gap-2">
              <label htmlFor="name">Kasa Adı *</label>
              <InputText
                id="name"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="Örn: Merkez Kasa"
              />
            </div>

            {/* 2. Para Birimi */}
            <div className="flex flex-col gap-2">
              <label htmlFor="currency">Para Birimi *</label>
              <Dropdown
                id="currency"
                value={formData.currency}
                options={CURRENCY_OPTIONS}
                onChange={(e) => handleChange('currency', e.value)}
                placeholder="Para Birimi Seçiniz"
              />
            </div>

          </div>
          
          {/* 3. Bakiye Bilgisi (Sadece Görüntüleme) */}
          {cash && (
              <div className="flex items-center gap-2 p-3 card font-semibold bg-blue-50 text-blue-800">
                  <DollarSign className="w-5 h-5"/>
                  <span>
                      Mevcut Bakiye: 
                      <span className="ml-2 font-bold text-lg">
                          {new Intl.NumberFormat('tr-TR', { style: 'currency', currency: cash.currency }).format(cash.balance)}
                      </span>
                  </span>
              </div>
          )}
        </div>
      )
    },
  ], [formData, cash]); 


  return (
    <Dialog
      header={cash ? "Kasa Düzenle" : "Yeni Kasa Ekle"}
      visible={isOpen}
      onHide={onClose}
      modal
        maximizable
      resizable={false}
      className="w-full lg:w-5/12 xl:w-3/12 min-h-[300px]" 
      footer={
        <div className="flex justify-end gap-3">
          <IconTextButton
            onClick={onClose}
            icon={X}
            className="btn-cancel"
          >
            İptal
          </IconTextButton>

          <IconTextButton
            onClick={handleSubmit}
            icon={cash ? Save : Plus}
            className="btn-new"
          >
            {cash ? "Güncelle" : "Kasa Ekle"}
          </IconTextButton>
        </div>
      }
    >
      <ETabs 
        tabs={tabs} 
        initialActiveIndex={activeIndex}
        onTabChange={setActiveIndex}
      />

    </Dialog>
  );
};